/**
 * Wishlist, stored in the browser.
 *
 * Shopify has no native wishlist and Civital did not want an app, so this keeps a
 * list of product handles in localStorage. That works for signed-out visitors,
 * needs no backend, and travels with the theme.
 *
 * The trade-off is real and worth stating: the list lives on one browser. It does
 * not follow a customer between devices and it goes if they clear site data.
 * Syncing to a customer account would need a server to write customer metafields,
 * which is the thing an app provides.
 *
 * Saved items are rendered by fetching each product's `wishlist-card` view, so the
 * cards on the wishlist page are the same Liquid — and the same CSS — as
 * everywhere else, rather than a second card implementation in JavaScript.
 */

const STORAGE_KEY = 'civital:wishlist';
const CHANGED = 'civital:wishlist:changed';

/** @returns {string[]} product handles, most recently added first */
export function readWishlist() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
  } catch {
    // Private browsing, disabled storage, or corrupted JSON. An empty wishlist is
    // the right answer in all three; never let this throw into a click handler.
    return [];
  }
}

function writeWishlist(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  } catch {
    // Storage unavailable or full. The in-page state below still updates, so the
    // control responds; it simply will not survive a reload.
  }
  document.dispatchEvent(new CustomEvent(CHANGED, { detail: { handles } }));
}

export function inWishlist(handle) {
  return readWishlist().includes(handle);
}

export function toggleWishlist(handle) {
  const handles = readWishlist();
  const index = handles.indexOf(handle);
  if (index === -1) handles.unshift(handle);
  else handles.splice(index, 1);
  writeWishlist(handles);
  return index === -1;
}

export function removeFromWishlist(handle) {
  writeWishlist(readWishlist().filter((h) => h !== handle));
}

/** The heart control on product pages and cards. */
class CivitalWishlistToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.handle = this.getAttribute('handle');
    if (!this.button || !this.handle) return;

    this.#render();
    this.button.addEventListener('click', this.#onClick);
    document.addEventListener(CHANGED, this.#render);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
    document.removeEventListener(CHANGED, this.#render);
  }

  #onClick = (event) => {
    event.preventDefault();
    toggleWishlist(this.handle);
  };

  #render = () => {
    const saved = inWishlist(this.handle);
    this.button.setAttribute('aria-pressed', String(saved));
    this.classList.toggle('civital-wishlist-button--saved', saved);
    const label = saved ? this.dataset.labelRemove : this.dataset.labelAdd;
    if (label) this.button.setAttribute('aria-label', label);
  };
}

/** The count beside the header heart. */
class CivitalWishlistCount extends HTMLElement {
  connectedCallback() {
    this.#render();
    document.addEventListener(CHANGED, this.#render);
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGED, this.#render);
  }

  #render = () => {
    const count = readWishlist().length;
    this.textContent = count > 0 ? String(count) : '';
    this.hidden = count === 0;
  };
}

/** The saved items on the wishlist page. */
class CivitalWishlistList extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.#render();
    document.addEventListener(CHANGED, this.#render);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGED, this.#render);
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const remove = event.target.closest('[data-wishlist-remove]');
    if (!remove) return;
    event.preventDefault();
    removeFromWishlist(remove.getAttribute('data-wishlist-remove'));
  };

  #render = async () => {
    const handles = readWishlist();

    if (this.empty) this.empty.hidden = handles.length > 0;
    if (!this.grid) return;

    if (handles.length === 0) {
      this.grid.replaceChildren();
      return;
    }

    const cards = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${Shopify?.routes?.root ?? '/'}products/${handle}?view=wishlist-card`);
          // A product that has been unpublished or renamed 404s. Drop it from the
          // list rather than leaving a permanently broken tile behind.
          if (!response.ok) {
            removeFromWishlist(handle);
            return null;
          }
          return await response.text();
        } catch {
          return null;
        }
      })
    );

    this.grid.innerHTML = cards.filter(Boolean).join('');
  };
}

if (!customElements.get('civital-wishlist-toggle')) {
  customElements.define('civital-wishlist-toggle', CivitalWishlistToggle);
}
if (!customElements.get('civital-wishlist-count')) {
  customElements.define('civital-wishlist-count', CivitalWishlistCount);
}
if (!customElements.get('civital-wishlist-list')) {
  customElements.define('civital-wishlist-list', CivitalWishlistList);
}
